// Authentication + session through RimoID (shared sign-in of the suite): web → API's oidc/login (backend-for-frontend),
// desktop → system browser + keychain (desktop/rimoid.ts). Access token in memory only. Mock mode keeps the local login.
import type { PermissionKey, PermissionLevel, SessionUser } from '@rimon/contracts';
import { api, API_URL, ApiError, MOCK, refreshSession, setSessionHooks, tokens } from './client';
import { isDesktop } from '../platform';
import { desktopLogin, desktopLogout } from '../desktop/rimoid';

export type { SessionUser };

type Listener = (u: SessionUser | null) => void;
let current: SessionUser | null = null;
const listeners = new Set<Listener>();

function setUser(u: SessionUser | null) {
  current = u;
  listeners.forEach((l) => l(u));
}
setSessionHooks({
  refreshed: (u) => u && setUser(u),
  // Role change or deactivation logs the user out immediately.
  lost: () => setUser(null),
  passwordRequired: () => current && !current.mustChangePassword && setUser({ ...current, mustChangePassword: true }),
});

export const session = {
  get user() {
    return current;
  },
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
};

export function level(u: SessionUser | null, key: PermissionKey): PermissionLevel {
  return (u?.permissions?.[key] as PermissionLevel) || 'NONE';
}

export function initials(u: SessionUser | null): string {
  if (!u) return '';
  return ((u.firstName?.[0] || '') + (u.lastName?.[0] || '')).toUpperCase() || u.email.slice(0, 2).toUpperCase();
}

export function displayName(u: SessionUser | null): string {
  if (!u) return '';
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email.split('@')[0];
}

interface TokenResponse {
  accessToken: string;
  expiresIn: number;
  refreshToken?: string;
  user: SessionUser;
}

/** method totp = code from the authenticator app (e-mail code only on request); email = a code was just sent to `to`. */
export type LoginResult = { status: 'ok'; user: SessionUser } | { status: '2fa_required'; challengeToken: string; method: 'totp' | 'email'; to?: string };

// Mock mode only; fields the backend adds later are filled with defaults by the screens.
const MOCK_USER = {
  id: 'mock', email: 'resid@rimon.lu', firstName: 'Resid', lastName: 'D.', roles: ['admin'], permissions: {},
  locale: 'de', dateFormat: 'dd.MM.yyyy', startPage: 'browser', mustChangePassword: false,
} as SessionUser;

function accept(res: TokenResponse): SessionUser {
  tokens.set(res.accessToken, res.refreshToken ?? null);
  setUser(res.user);
  return res.user;
}

/** Step 1 — e-mail + password (+ "Angemeldet bleiben"). */
export async function login(email: string, password: string, remember: boolean): Promise<LoginResult> {
  if (MOCK) {
    if (password.length < 4) throw new ApiError(401, 'invalid_credentials', 'Invalid credentials');
    return { status: '2fa_required', challengeToken: 'mock', method: 'totp' };
  }
  const res = await api<{ status: '2fa_required'; challengeToken: string; method?: 'totp' | 'email'; to?: string } | ({ status: 'ok' } & TokenResponse)>('/auth/login', {
    method: 'POST',
    auth: false,
    body: { email, password, remember },
  });
  if (res.status === '2fa_required') return { ...res, method: res.method ?? 'totp' };
  return { status: 'ok', user: accept(res) };
}

/** Step 2 — 6-digit code from the authenticator app. */
export async function verifyTotp(challengeToken: string, code: string): Promise<SessionUser> {
  if (MOCK) {
    if (code === '000000') throw new ApiError(401, 'invalid_code', 'Invalid code');
    setUser(MOCK_USER);
    return MOCK_USER;
  }
  return accept(await api<TokenResponse>('/auth/2fa/verify', { method: 'POST', auth: false, body: { challengeToken, code } }));
}

/** 2FA step „Code erneut senden“: a code by e-mail (works instead of the app's code). */
export async function sendLoginCode(challengeToken: string): Promise<{ sent: boolean; to: string }> {
  if (MOCK) return { sent: true, to: 'm…@rimon.test' };
  return api('/auth/2fa/send', { method: 'POST', auth: false, body: { challengeToken } });
}

// ---------- Passkeys: only on explicit request (profile „Passkey hinzufügen“, login „Mit Passkey anmelden“) ----------
export interface Passkey { id: string; name: string; createdAt: string; lastUsedAt: string | null }
/** WebAuthn needs a secure context (https or localhost) and a browser/WebView that supports it. */
export const passkeysSupported = () => typeof window !== 'undefined' && window.isSecureContext && typeof window.PublicKeyCredential === 'function';
/** Login with a passkey (replaces password + second factor). */
export async function loginWithPasskey(remember: boolean): Promise<SessionUser> {
  const { startAuthentication } = await import('@simplewebauthn/browser');
  const { options, state } = await api<{ options: Parameters<typeof startAuthentication>[0]['optionsJSON']; state: string }>('/auth/passkeys/login/options', { method: 'POST', auth: false, body: { remember } });
  const response = await startAuthentication({ optionsJSON: options });
  return accept(await api<TokenResponse>('/auth/passkeys/login', { method: 'POST', auth: false, body: { state, response } }));
}


/** First login with a temporary password: replaces the (now revoked) session. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<SessionUser> {
  if (MOCK) {
    const u = { ...(current as SessionUser), mustChangePassword: false };
    setUser(u);
    return u;
  }
  // RimoID ends every other session; this one stays (the API names its sid).
  const res = await api<{ user: SessionUser }>('/auth/password/change', { method: 'POST', body: { currentPassword, newPassword } });
  setUser(res.user);
  return res.user;
}

/** Reset link from the e-mail: `#/reset-password?token=…`. */
export async function resetPassword(token: string, password: string): Promise<void> {
  if (MOCK) return;
  await api('/auth/password/reset', { method: 'POST', auth: false, body: { token, password } });
}

export type UserPrefs = Partial<Pick<SessionUser, 'locale' | 'dateFormat' | 'startPage'>> & { preferences?: Record<string, unknown> };

/** Saves personal settings; `preferences` is merged flat on the server (null deletes a key). */
export async function savePreferences(p: UserPrefs): Promise<void> {
  if (MOCK || !current) return;
  const u = await api<SessionUser>('/auth/me/preferences', { method: 'PATCH', body: p });
  current = u; // no listener fan-out: the screens already show the new values
}

/** Always resolves (the server answers 204 whether or not the account exists). */
export async function requestPasswordReset(email: string): Promise<void> {
  if (MOCK) return;
  await api('/auth/password/forgot', { method: 'POST', auth: false, body: { email } });
}

/** Restores the session on app start (refresh cookie / stored desktop token). */
export async function restoreSession(): Promise<SessionUser | null> {
  if (MOCK) return current;
  if (!(await refreshSession())) return null;
  try {
    const user = await api<SessionUser>('/auth/me');
    setUser(user);
    return user;
  } catch {
    return null;
  }
}

/**
 * Set by „Abmelden“: the sign-in screen then waits for „Wieder anmelden“ instead of starting RimoID on its own (the
 * RimoID session is still alive and would sign straight back in). Kept in localStorage so it survives the redirect to
 * `#/login` and a restart of the desktop app; cleared when the person signs in again.
 */
const SIGNED_OUT_KEY = 'rimocompta:signed-out';
const signedOutHow = () => { try { return localStorage.getItem(SIGNED_OUT_KEY); } catch { return null; } };
export const signedOutByUser = () => !!signedOutHow();
/** True when RimoID ended the sign-in (not „Abmelden“ here): the screen must not promise that the RimoID session lives on. */
export const signedOutByRimoid = () => signedOutHow() === 'rimoid';
const markSignedOut = (on: boolean, how = '1') => { try { if (on) localStorage.setItem(SIGNED_OUT_KEY, how); else localStorage.removeItem(SIGNED_OUT_KEY); } catch { /* storage blocked */ } };
/** RimoID ended the session (data/sessionCheck): show „Abgemeldet“ and wait for a click, as after „Abmelden“. */
export const holdSignIn = (on: boolean) => { if (!MOCK) markSignedOut(on, 'rimoid'); };

/**
 * Sign-in at RimoID. Web: the API (backend-for-frontend) sends the browser to RimoID and back to `returnTo`.
 * Desktop: RimoID opens in the system browser (shared session with all suite programs), the app waits for it.
 * `selectAccount`: after „Abmelden“ RimoID first shows „Konto auswählen“ (prompt=select_account).
 */
export async function startSignIn(returnTo = '#/', selectAccount = false): Promise<SessionUser | null> {
  if (MOCK) return null;
  markSignedOut(false);
  const lang = document.documentElement.lang === 'fr' ? 'fr' : undefined;
  if (isDesktop()) {
    const t = await desktopLogin(lang, selectAccount);
    tokens.set(t.accessToken);
    const user = await api<SessionUser>('/auth/me');
    setUser(user);
    return user;
  }
  const q = new URLSearchParams({ origin: location.origin, returnTo, ...(lang ? { lang } : {}), ...(selectAccount ? { prompt: 'select_account' } : {}) });
  location.assign(`${API_URL}/auth/oidc/login?${q}`);
  return null;
}

/** „Abmelden“ ends the session at RimoID too – and with it in every program of the suite. */
/**
 * Signs out of RimoCompta only: the refresh token is revoked (web: by the API, desktop: by Rust, keychain cleared); the
 * RimoID session stays, so „Wieder anmelden“ needs no password. Signing out everywhere is done in RimoID.
 * Afterwards the sign-in screen waits for a click instead of signing straight back in (`signedOutByUser`).
 */
export async function logout(): Promise<void> {
  try {
    if (MOCK) { /* nothing on a server */ }
    else if (isDesktop()) await desktopLogout();
    else await api('/auth/logout', { method: 'POST', auth: false, body: {} });
  } catch {
    /* the local session is dropped either way */
  }
  tokens.set(null, null);
  if (!MOCK) { markSignedOut(true); location.hash = '#/login'; }
  setUser(null);
}

/** German error text for the login form, in the design's wording. */
export function loginErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 0) return 'Server nicht erreichbar. Bitte später erneut versuchen.';
    if (e.code === 'code_recently_sent') return 'Ein Code wurde gerade verschickt. Bitte kurz warten.';
    if (e.code === 'account_locked') return 'Zu viele Fehlversuche für dieses Konto. Bitte in 15 Minuten erneut versuchen.';
    if (e.code === 'rate_limited' || e.status === 429) return 'Zu viele Versuche. Bitte warten Sie einen Moment.';
    if (e.code === 'invalid_code' || e.code === 'challenge_expired') return 'Code ungültig oder abgelaufen.';
    if (e.code === 'invalid_credentials' || e.code === 'validation_failed') return 'E-Mail oder Passwort ist falsch.';
    if (e.code === 'invalid_current_password') return 'Das aktuelle Passwort ist falsch.';
    if (e.code === 'password_unchanged') return 'Das neue Passwort muss sich vom alten unterscheiden.';
    if (e.code === 'temporary_password_expired') return 'Das Einmal-Passwort ist abgelaufen. Bitte lassen Sie sich vom Administrator neu einladen.';
    if (e.code === 'totp_not_started') return 'Bitte die Einrichtung neu starten.';
    if (e.code === 'passkey_unknown') return 'Dieser Passkey ist für RimoCompta nicht registriert.';
    if (e.code === 'passkey_invalid') return 'Der Passkey konnte nicht geprüft werden. Bitte erneut versuchen.';
    if (e.code === 'passkey_expired') return 'Die Anfrage ist abgelaufen. Bitte erneut versuchen.';
    if (e.code === 'passkey_exists') return 'Dieser Passkey ist bereits hinterlegt.';
    if (e.code === 'totp_already_enabled') return 'Zwei-Faktor-Anmeldung ist schon eingeschaltet. Zum Wechseln erst ausschalten.';
    if (e.code === 'invalid_reset_token') return 'Der Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.';
  }
  return 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.';
}

// Development: this module holds the session (user, tokens, listeners). A hot update would leave the running screens
// on the old copy (stale user, „lost“ reaching nobody), so the page reloads instead.
if (import.meta.hot) import.meta.hot.accept(() => location.reload());
